import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, UserPlus } from "lucide-react";
import { fetchGroup } from "@/lib/api";
import { ClickableImage } from "@/components/Lightbox";
import JoinModal from "@/components/JoinModal";

const GroupDetail = () => {
    const { slug } = useParams();
    const [g, setG] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joinOpen, setJoinOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetchGroup(slug)
            .then((d) => alive && setG(d))
            .catch(() => alive && setG(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [slug]);

    return (
        <div className="min-h-screen">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14" data-testid={`group-detail-${slug}`}>
                <Link
                    to="/groups"
                    className="inline-flex items-center gap-2 text-sm hover:opacity-90"
                    style={{ color: "#D4AF37" }}
                >
                    <ArrowLeft size={14} /> Back to all groups
                </Link>

                {loading ? (
                    <p className="mt-8 italic" style={{ color: "#CCCCCC" }}>Loading…</p>
                ) : !g ? (
                    <p className="mt-8 italic" style={{ color: "#CCCCCC" }}>Group not found.</p>
                ) : (
                    <>
                        <div className="mt-6">
                            <span
                                className="text-[11px] font-semibold uppercase tracking-widest"
                                style={{ color: "#D4AF37" }}
                            >
                                {g.category === "scc" ? "Small Christian Community"
                                    : g.category === "ecclesiastical" ? "Ecclesiastical Group"
                                    : g.category === "ministry" ? "Parish Ministry"
                                    : "Youth Group"}
                            </span>
                            <h1 className="mt-2 text-4xl sm:text-5xl font-serif font-semibold" style={{ color: "#FFFFFF" }}>
                                {g.name}
                            </h1>
                        </div>

                        <div className="gold-divider my-6 opacity-60" />

                        {g.image_url && (
                            <div className="mb-8 rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(212, 175, 55, 0.4)" }}>
                                <ClickableImage
                                    src={g.image_url}
                                    alt={g.category === "scc" ? `Patron saint image, ${g.name}, Holy Trinity Kileleshwa` : `${g.name}, Holy Trinity Kileleshwa`}
                                    className="w-full max-h-[500px] object-contain bg-black"
                                />
                            </div>
                        )}

                        <div
                            className="rounded-2xl p-6"
                            style={{ background: "var(--htcc-surface)", border: "1px solid rgba(212, 175, 55, 0.2)" }}
                        >
                            {g.description ? (
                                <p
                                    data-testid="group-description"
                                    className="text-base sm:text-lg leading-relaxed whitespace-pre-wrap"
                                    style={{ color: "#CCCCCC" }}
                                >
                                    {g.description}
                                </p>
                            ) : (
                                <p className="italic text-sm" style={{ color: "#CCCCCC" }}>
                                    A description of this group will appear here soon. Please contact the Parish Office for details.
                                </p>
                            )}
                        </div>

                        {g.member_photo_url && (
                            <div className="mt-8">
                                <p className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: "#D4AF37" }}>
                                    Members
                                </p>
                                <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(212, 175, 55, 0.4)" }}>
                                    <ClickableImage
                                        src={g.member_photo_url}
                                        alt={`${g.name} members, Holy Trinity Kileleshwa`}
                                        className="w-full max-h-[600px] object-cover cursor-zoom-in"
                                        data-testid={`group-member-photo-${slug}`}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Public sub-page intentionally omits Meeting Location and Moderator/Vice-Moderator contacts (parish feedback). Admin can still edit those fields in the dashboard. */}

                        <div className="mt-8">
                            <button
                                type="button"
                                onClick={() => setJoinOpen(true)}
                                data-testid={`group-detail-join-${slug}`}
                                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold"
                                style={{ background: "#D4AF37", color: "#3d2e2c" }}
                            >
                                <UserPlus size={16} /> Join {g.name}
                            </button>
                        </div>
                    </>
                )}
            </div>
            <JoinModal open={joinOpen} onClose={() => setJoinOpen(false)} groupName={g?.name || ""} />
        </div>
    );
};

export default GroupDetail;
